import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LoadingState } from './LoadingState';
import { ApprovalUI } from './ApprovalUI';
import { QRDisplay } from './QRDisplay';
import { usePairingActions } from './usePairingActions';
import { usePairingPolling } from './usePairingPolling';
import { CrossDeviceAuthData, PairingStatus } from './types';
import { StyledButton } from '../styles/StyledButtons';
import StyledSection from '../styles/StyledSection';

export default function CrossDeviceLogin() {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authData, setAuthData] = useState<CrossDeviceAuthData | null>(null);
  const [pairingStatus, setPairingStatus] = useState<PairingStatus>('pending');
  const [showApproval, setShowApproval] = useState(false);
  const [correctChoice, setCorrectChoice] = useState<string>('');
  const [deviceInfo, setDeviceInfo] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);

  // Custom hooks for actions and polling
  const { startPairing, handleApprove, handleDeny } = usePairingActions(
    authData,
    setLoading,
    setError,
    setAuthData,
    setPairingStatus,
    setShowApproval,
    setCorrectChoice,
  );

  usePairingPolling(
    authData,
    setPairingStatus,
    setShowApproval,
    setCorrectChoice,
    setDeviceInfo,
    setTimeLeft,
    setError,
  );

  // Initialize pairing on component mount
  useEffect(() => {
    startPairing();
  }, [startPairing]);

  // No automatic redirect - just show success message
  // User can navigate away manually when ready

  // Loading and error states
  const loadingState = LoadingState({
    loading,
    error,
    onRetry: startPairing,
  });

  if (loadingState || !authData) {
    return loadingState;
  }

  return (
    <StyledSection
      tone="default"
      sx={{
        maxWidth: 400,
        mx: 'auto',
        backgroundColor: 'background.paper',
        borderRadius: 2,
        boxShadow: 2,
      }}
    >
      <Box sx={{ p: 3 }}>
        {pairingStatus === 'redeemed' ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ fontSize: 48, color: 'success.main', mb: 2 }}>✓</Box>
            <Box sx={{ fontSize: 18, fontWeight: 'bold', color: 'success.main', mb: 1 }}>
              Login Complete!
            </Box>
            <Box sx={{ fontSize: 14, color: 'text.secondary' }}>
              You are now logged in on your mobile device.
            </Box>
          </Box>
        ) : pairingStatus === 'scanned' || showApproval ? (
          <ApprovalUI
            correctChoice={correctChoice}
            deviceInfo={deviceInfo}
            onApprove={handleApprove}
            onDeny={handleDeny}
          />
        ) : (
          <QRDisplay authData={authData} timeLeft={timeLeft} />
        )}
      </Box>

      {pairingStatus !== 'redeemed' && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
          <StyledButton
            size="small"
            startIcon={<RefreshIcon />}
            onClick={startPairing}
            disabled={loading}
            buttonVariant="inactiveOrCancel"
            noTopSpacing={true}
          >
            Generate New QR Code
          </StyledButton>
        </Box>
      )}
    </StyledSection>
  );
}
